import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

// Un pase espeja a una caución (misma carga comercial + operación del trader),
// pero es siempre a 24hs (se guarda fechaVencimiento como string 'YYYY-MM-DD',
// editable por feriados) y lleva un array de activos disponibles que el comercial
// carga y el trader "usa" al operar. El CRUD se centraliza acá (patrón
// licitacionesService); ningún componente escribe la colección directo.

const COLLECTION = 'pases';

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Sanea el array de activos: cada elemento con especie (upper), cantidad >= 0 y
// cantidadUsada acotada a [0, cantidad]. Descarta filas sin especie.
const sanitizeActivos = (activos) =>
  (Array.isArray(activos) ? activos : [])
    .filter((a) => a && String(a.especie || '').trim())
    .map((a, index) => {
      const cantidad = toNumber(a.cantidad);
      const cantidadUsada = Math.min(Math.max(toNumber(a.cantidadUsada), 0), cantidad);
      return {
        id: a.id || `act-${index + 1}`,
        especie: String(a.especie).trim().toUpperCase(),
        cantidad,
        cantidadUsada,
      };
    });

/**
 * Crea un pase completando todos los campos requeridos por las reglas
 * (estado 'Pendiente', operado en 0, etc.) para que los updates parciales
 * posteriores pasen la validación de isValidPase.
 * @param {object} data - { operacion, nroComitente, cliente, fechaVencimiento, monto, tasa, moneda, mercado, comercial, comentarioComercial, activos }
 * @param {{ userId: string, userName: string }} actor
 */
export const createPase = (data, { userId, userName }) => {
  try {
    return addDoc(collection(db, COLLECTION), {
      operacion: data.operacion,
      nroComitente: data.nroComitente,
      cliente: data.cliente,
      fechaVencimiento: data.fechaVencimiento,
      monto: Math.ceil(toNumber(data.monto)),
      tasa: toNumber(data.tasa),
      moneda: data.moneda,
      mercado: data.mercado,
      comercial: data.comercial,
      comentarioComercial: data.comentarioComercial || '',
      activos: sanitizeActivos(data.activos),
      createdAt: serverTimestamp(),
      creadoPorNombre: userName,
      creadoPorId: userId,
      updatedAt: serverTimestamp(),
      estado: 'Pendiente',
      comentarioTrader: '',
      montoOperado: 0,
      tasaOperada: 0,
      operador: '',
    });
  } catch (error) {
    console.error('pasesService.createPase failed:', error);
    throw error;
  }
};

/**
 * Edita los campos de carga de un pase (rol Comercial, estado Pendiente).
 */
export const updatePase = (id, data) => {
  try {
    const payload = { ...data, updatedAt: serverTimestamp() };
    if ('monto' in payload) payload.monto = Math.ceil(toNumber(payload.monto));
    if ('tasa' in payload) payload.tasa = toNumber(payload.tasa);
    if ('activos' in payload) payload.activos = sanitizeActivos(payload.activos);
    return updateDoc(doc(db, COLLECTION, id), payload);
  } catch (error) {
    console.error('pasesService.updatePase failed:', { id, error });
    throw error;
  }
};

/**
 * Soft delete: marca estado 'Eliminada' y sanea numéricos (patrón softDeleteCaucion).
 */
export const softDeletePase = (pase, { userId, userName }) => {
  try {
    return updateDoc(doc(db, COLLECTION, pase.id), {
      estado: 'Eliminada',
      eliminadoPorId: userId,
      eliminadoPorNombre: userName,
      eliminadoAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      monto: toNumber(pase.monto),
      tasa: toNumber(pase.tasa),
    });
  } catch (error) {
    console.error('pasesService.softDeletePase failed:', { id: pase?.id, error });
    throw error;
  }
};

/**
 * Cambia el estado de un pase (Trader). 'En Proceso' asigna operador;
 * 'Pendiente' lo libera. No usar para 'Hecha'/'Rechazada' (ver operarPase/rechazarPase).
 */
export const setPaseEstado = (pase, estado, { userName }) => {
  try {
    const payload = { estado, updatedAt: serverTimestamp() };
    if (estado === 'En Proceso') payload.operador = userName;
    else if (estado === 'Pendiente') payload.operador = '';
    return updateDoc(doc(db, COLLECTION, pase.id), payload);
  } catch (error) {
    console.error('pasesService.setPaseEstado failed:', { id: pase?.id, error });
    throw error;
  }
};

export const rechazarPase = (pase, comentarioTrader, { userName }) => {
  try {
    return updateDoc(doc(db, COLLECTION, pase.id), {
      estado: 'Rechazada',
      comentarioTrader,
      operador: userName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('pasesService.rechazarPase failed:', { id: pase?.id, error });
    throw error;
  }
};

/**
 * Opera un pase: lo marca 'Hecha' con monto/tasa operados y los activos usados
 * elegidos por el trader (un solo update atómico).
 * @param {object} pase
 * @param {{ montoOperado: any, tasaOperada: any, activos: Array }} operado
 * @param {{ userName: string }} actor
 */
export const operarPase = (pase, { montoOperado, tasaOperada, activos }, { userName }) => {
  try {
    return updateDoc(doc(db, COLLECTION, pase.id), {
      estado: 'Hecha',
      operador: userName,
      montoOperado: Math.ceil(toNumber(montoOperado)),
      tasaOperada: toNumber(tasaOperada),
      activos: sanitizeActivos(activos),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('pasesService.operarPase failed:', { id: pase?.id, error });
    throw error;
  }
};

/**
 * Actualiza solo las cantidades usadas de los activos (Trader, fuera del flujo
 * de marcar 'Hecha'): permite ir registrando lo usado durante el día.
 */
export const updateActivosUsados = (paseId, activos) => {
  try {
    return updateDoc(doc(db, COLLECTION, paseId), {
      activos: sanitizeActivos(activos),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('pasesService.updateActivosUsados failed:', { id: paseId, error });
    throw error;
  }
};
